/**
 * Empty State Component
 *
 * Generic component to display when a list or content area is empty.
 * Supports filtered vs empty catalog states with optional action button.
 */

import type { ReactNode, ElementType } from 'react';
import { Package } from 'lucide-react';
import { Button } from '../Button';
import { cn } from '../../lib/utils';

export interface EmptyStateProps {
  /** Icon to display (defaults to Package) */
  icon?: ElementType;
  /** Title text */
  title: string;
  /** Description text (can be multiline) */
  description?: string | ReactNode;
  /** Primary action button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: ElementType;
  };
  /** Additional class names */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: {
    container: 'p-6',
    iconWrapper: 'p-3',
    icon: 'w-6 h-6',
    title: 'text-base',
    description: 'text-xs',
    gap: 'mb-3',
  },
  md: {
    container: 'p-8',
    iconWrapper: 'p-4',
    icon: 'w-8 h-8',
    title: 'text-lg',
    description: 'text-sm',
    gap: 'mb-4',
  },
  lg: {
    container: 'p-12',
    iconWrapper: 'p-5',
    icon: 'w-10 h-10',
    title: 'text-xl',
    description: 'text-base',
    gap: 'mb-6',
  },
};

export function EmptyState({
  icon: Icon = Package,
  title,
  description,
  action,
  className,
  size = 'md',
}: EmptyStateProps) {
  const styles = sizeStyles[size];

  return (
    <div
      className={cn(
        'bg-white border border-neutral-200 rounded-xl shadow-sm text-center',
        styles.container,
        className
      )}
    >
      <div className={cn('flex justify-center', styles.gap)}>
        <div className={cn('bg-neutral-100 rounded-full', styles.iconWrapper)}>
          <Icon className={cn('text-neutral-400', styles.icon)} />
        </div>
      </div>

      <h3 className={cn('font-medium text-dark-500 mb-2', styles.title)}>
        {title}
      </h3>

      {description && (
        <div className={cn('text-neutral-500', styles.description, action ? 'mb-6' : '')}>
          {typeof description === 'string' ? <p>{description}</p> : description}
        </div>
      )}

      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.icon && <action.icon className="w-4 h-4" />}
          {action.label}
        </Button>
      )}
    </div>
  );
}
